import VueCookie from 'vue-cookie';
import axios, { AxiosInstance } from 'axios';
import { ENDPOINTS } from '@/settings/endpoints';

const d = VueCookie.get('d', { domain: '.descomplica.com.br' });

export interface CollaborationResponse<T> {
  data: T;
  message: string;
  identifier: string;
}

export enum ActivityType {
  PRACTICE_EXERCISE = 'practiceExercise',
  DISCURSIVE_EXERCISE = 'discursiveExercise',
}

export enum ActivityItemStatus {
  DONE = 'DONE',
  PENDING = 'PENDING',
  BLOCKED = 'BLOCKED',
  WAITING = 'WAITING',
  DONE_DELAYED = 'DONE_DELAYED',
  PENDING_DELAYED = 'PENDING_DELAYED',
  WAITING_DELAYED = 'WAITING_DELAYED',
}

export interface UserExerciseAttachments {
  name: string;
  size: number;
  type: string;
  url: string;
}

export interface ActivityAnswer {
  activityType: ActivityType;
  questionId: number;
  body: string;
  attemptCount?: number;
  attachments?: UserExerciseAttachments[];
}

export interface ActivityUpdateAnswer extends ActivityAnswer {
  textId: number;
  commentId: number;
}

export interface ActivityItem {
  listSlug: string;
  disciplineSlug: string;
  status: ActivityItemStatus | string;
  questionId: number;
}

export interface GradeComponentDetails {
  grade: number;
  maxGrade: number;
}

export interface UserBaseGrades {
  grade: number;
}

export interface UserActivitiesDetails extends UserBaseGrades {
  gradeComponents?: {
    achievement: GradeComponentDetails | null;
    objectiveExercise: GradeComponentDetails | null;
    discursiveExercise?: GradeComponentDetails | null;
    practiceExercise?: GradeComponentDetails | null;
    genericDiscursiveExercise?: GradeComponentDetails | null;
    reviewList: GradeComponentDetails | null;
    finalExam: GradeComponentDetails | null;
  };
  activitiesStatus?: {
    objectiveExercises: Array<ActivityItem>;
    discursiveExercises?: Array<ActivityItem> | null;
    practiceExercises?: Array<ActivityItem> | null;
    genericDiscursiveExercises?: Array<ActivityItem> | null;
    reviewLists: Array<ActivityItem>;
    finalExams?: Array<ActivityItem>;
  };
}

export interface UserPersistedGrade extends UserBaseGrades {
  userId: string;
  classroomId: number;
  objectiveExercise: number;
  discursiveExercise: number;
  practiceExercise: number;
  reviewList: number;
  achievement: number;
  exam: number;
  replacementExam: number;
}

const encodeAssertionId = (id: number): string => btoa(id.toString().split('').reverse().join(''));

const decodeAssertionId = (id: string): number =>
  Number.parseInt(atob(id).split('').reverse().join(''), 10);

export interface UserListActivityStatus {
  listId: number;
  status: ActivityItemStatus;
  answeredAt?: Date;
  dueDate?: Date;
}

export interface UserListStatus {
  objectiveExercises?: UserListActivityStatus;
  discursiveExercises?: UserListActivityStatus;
  practiceExercises?: UserListActivityStatus;
  reviewLists?: UserListActivityStatus;
}

export class UndergradGradesService {
  private pepeLegalUndergrad: AxiosInstance;

  constructor() {
    this.pepeLegalUndergrad = axios.create({
      baseURL: ENDPOINTS.pegasusPepeLegal.UNDERGRAD,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${d}`,
      },
    });
  }

  async getPersistedDisciplineGrade(classroomId: number): Promise<UserActivitiesDetails> {
    const { data: collabResponse } = await this.pepeLegalUndergrad.get<
      CollaborationResponse<UserPersistedGrade>
    >(`/persisted-grades/${classroomId}`);

    const discursiveGrade = collabResponse.data.discursiveExercise;
    const practiceGrade = collabResponse.data.practiceExercise;

    let discursiveExercise: any = {
      genericDiscursiveExercise: {
        grade: 0,
        maxGrade: 4,
      },
    };

    if (discursiveGrade > 0 && practiceGrade <= 0) {
      discursiveExercise = {
        discursiveExercise: {
          grade: discursiveGrade,
          maxGrade: 4,
        },
      };
    } else if (practiceGrade > 0 && discursiveGrade <= 0) {
      discursiveExercise = {
        practiceExercise: {
          grade: practiceGrade,
          maxGrade: 4,
        },
      };
    }

    const response: UserActivitiesDetails = {
      grade: collabResponse.data.grade,
      gradeComponents: {
        achievement: {
          grade: collabResponse.data.achievement,
          maxGrade: 1,
        },
        objectiveExercise: {
          grade: collabResponse.data.objectiveExercise,
          maxGrade: 2,
        },
        ...discursiveExercise,
        reviewList: {
          grade: collabResponse.data.reviewList,
          maxGrade: 3,
        },
        finalExam: {
          grade:
            collabResponse.data.exam > 0
              ? collabResponse.data.exam
              : collabResponse.data.replacementExam,
          maxGrade: 2,
        },
      },
    };

    return response;
  }

  async getDisciplineGrades(classroomId: number): Promise<UserActivitiesDetails> {
    const { data: collabResponse } = await this.pepeLegalUndergrad.get<
      CollaborationResponse<UserActivitiesDetails>
    >(`/user-activities-details/${classroomId}`);

    return collabResponse.data;
  }

  async getAssertionsCorrections(assertionIds: number[]): Promise<any> {
    const assertionsParam = assertionIds.map(encodeAssertionId).join('|');

    const { data: collabResponse } = await this.pepeLegalUndergrad.get<CollaborationResponse<any>>(
      `/questions/assertions-corrections`,
      {
        params: { assertions: assertionsParam },
      },
    );

    const assertionsDecoded = collabResponse.data.map(({ id, correct }: any) => ({
      id: decodeAssertionId(id),
      correct,
    }));

    return assertionsDecoded;
  }

  async getListStatus(classroomId: number, listId: number): Promise<UserListStatus> {
    const { data: collabResponse } = await this.pepeLegalUndergrad.get<
      CollaborationResponse<UserListStatus>
    >('/user-list-status/', { params: { classroomId, listId } });

    return collabResponse.data;
  }

  async createActivityAnswer(activityAnswer: ActivityAnswer): Promise<any> {
    const token = localStorage.getItem('token');
    const { data: collabResponse } = await this.pepeLegalUndergrad.post<
      CollaborationResponse<ActivityAnswer>
    >(
      '/send-activity-answer-to-evaluate/',
      {
        body: activityAnswer.body,
        activityType: activityAnswer.activityType,
        questionId: activityAnswer.questionId,
        attemptCount: activityAnswer.attemptCount,
        attachments: activityAnswer.attachments,
      },
      {
        headers: {
          'Authorization-dex': `Bearer ${token}`,
        },
      },
    );

    return collabResponse.data;
  }

  async updateActivityAnswer(activityUpdateAnswer: ActivityUpdateAnswer): Promise<any> {
    const token = localStorage.getItem('token');

    const { data: collabResponse } = await this.pepeLegalUndergrad.post<
      CollaborationResponse<UserListStatus>
    >(
      '/update-activity-answer-to-evaluate/',
      {
        body: activityUpdateAnswer.body,
        questionId: activityUpdateAnswer.questionId,
        attachments: activityUpdateAnswer.attachments,
        textId: activityUpdateAnswer.textId,
        commentId: activityUpdateAnswer.commentId,
      },
      {
        headers: {
          'Authorization-dex': `Bearer ${token}`,
        },
      },
    );

    return collabResponse.data;
  }
}
